import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const bannerVariants = cva(
  "flex flex-col gap-1 rounded-xl border px-[16px] py-[12px] text-sm leading-relaxed",
  {
    variants: {
      variant: {
        info:
          "bg-[color:var(--bg-brand-primary)] border-[color:var(--bg-brand-secondary)] text-[color:var(--text-brand-secondary)]",
        success:
          "bg-[color:var(--bg-success-primary)] border-[color:var(--bg-success-secondary)] text-[color:var(--text-success)]",
        warn:
          "bg-[color:var(--bg-warning-primary)] border-[color:var(--bg-warning-secondary)] text-[color:var(--text-warning)]",
        danger:
          "bg-[color:var(--bg-error-primary)] border-[color:var(--border-error)] text-[color:var(--text-error)]",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

export interface BannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof bannerVariants> {
  title?: React.ReactNode;
}

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      className,
      variant = "info",
      title,
      children,
      role,
      "aria-live": ariaLive,
      "aria-atomic": ariaAtomic,
      ...props
    },
    ref,
  ) => {
    const resolvedRole = role ?? (variant === "warn" || variant === "danger" ? "alert" : "status");
    // Explicit live-region semantics (Story 7.7 a11y fold-in). The OfflineBanner is the first
    // consumer whose text can mutate IN PLACE, and Firefox / older AT don't reliably re-announce
    // from the implicit live semantics of role="alert"/"status" alone. A caller-supplied
    // `aria-live` always wins (the normalized override contract — previously `role` was honoured
    // explicitly but `aria-live` only flowed through the spread); otherwise it tracks the role
    // (alert → assertive, status → polite). `aria-atomic` re-announces the whole banner on change.
    const resolvedAriaLive = ariaLive ?? (resolvedRole === "alert" ? "assertive" : "polite");
    const resolvedAriaAtomic = ariaAtomic ?? true;
    const hasTitle = title != null && title !== false && title !== "";
    return (
      <div
        ref={ref}
        className={cn(bannerVariants({ variant }), className)}
        {...props}
        role={resolvedRole}
        aria-live={resolvedAriaLive}
        aria-atomic={resolvedAriaAtomic}
      >
        {hasTitle ? <strong className="font-semibold">{title}</strong> : null}
        {children != null ? <div className="text-[color:var(--text-secondary)]">{children}</div> : null}
      </div>
    );
  },
);
Banner.displayName = "Banner";

export { Banner, bannerVariants };
